import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LogOut, Mail, Trash2, User as UserIcon, Link2, Sparkles, KeyRound, Crown } from "lucide-react";
import { api, apiBase, setAuthToken, type LlmProvider } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { NativeSelect } from "@/components/ui/native-select";
import { useAuth } from "@/hooks/useAuth";
import { usePlan } from "@/hooks/usePlan";
import { useSeo } from "@/lib/useSeo";
import { LoginRequired } from "@/components/LoginRequired";
import { openUpgrade } from "@/components/UpgradeDialog";
import { fromNowFR } from "@/lib/utils";

export default function AccountPage() {
  useSeo({ title: "Mon compte", canonicalPath: "/account", noindex: true });
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div className="text-sm text-foreground-muted">Chargement…</div>;
  }
  if (!isAuthenticated || !user) {
    return (
      <LoginRequired
        icon={UserIcon}
        title="Connectez-vous pour gérer votre compte"
        description="Vos préférences, votre compte Boursorama lié et vos notifications."
      />
    );
  }
  return <AccountContent />;
}

function AccountContent() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["me"], queryFn: () => api.me() });
  const user = data?.user;

  // Returning from Stripe Checkout. The webhook is the source of truth for the
  // plan flip ; here we just refresh /me a couple of times and confirm to the
  // user (the webhook may land a moment after the redirect).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgraded") === "1") {
      toast.success("Bienvenue dans Boursicote Pro 🎉");
      qc.invalidateQueries({ queryKey: ["me"] });
      setTimeout(() => qc.invalidateQueries({ queryKey: ["me"] }), 3000);
      window.history.replaceState({}, "", "/account");
    } else if (params.get("canceled") === "1") {
      toast.info("Paiement annulé — vous restez sur le plan gratuit.");
      window.history.replaceState({}, "", "/account");
    }
  }, [qc]);

  async function set(body: Parameters<typeof api.updatePreferences>[0]) {
    try {
      await api.updatePreferences(body);
      await qc.invalidateQueries({ queryKey: ["me"] });
      toast.success("Préférence enregistrée.");
    } catch {
      toast.error("Erreur.");
    }
  }

  async function unlinkBourso() {
    try {
      await api.unlinkBourso();
      await qc.invalidateQueries({ queryKey: ["me"] });
      toast.success("Compte Boursorama délié.");
    } catch {
      toast.error("Erreur.");
    }
  }

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <h1 className="text-2xl font-semibold text-foreground">Mon compte</h1>

      {/* Subscription */}
      <PlanCard />

      {/* Identity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="size-4" />
            Identité
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-1">
          <p className="text-sm text-foreground">{user?.email}</p>
          <p className="text-xs text-foreground-muted">
            Connexion par code email, sans mot de passe.
          </p>
        </CardContent>
      </Card>

      {/* Linked Boursorama account */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Link2 className="size-4" />
            Compte Boursorama
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-3">
          {user?.bourso_user_id ? (
            <>
              <p className="text-sm text-foreground">Compte lié (#{user.bourso_user_id}).</p>
              <Button size="sm" variant="outline" onClick={unlinkBourso}>
                Délier
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-foreground-muted">
                Aucun compte lié. Liez-le depuis la page Utilisateurs.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Notifications</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Row
            label="Récap email quotidien"
            desc="Un résumé du matin de l'activité sur vos suivis (forums, fils, utilisateurs)."
            value={user?.digest_enabled ?? true}
            onChange={(v) => set({ digest_enabled: v })}
          />
          <Row
            label="Newsletter produit"
            desc="Nouveautés et conseils, occasionnellement."
            value={user?.newsletter_opt_in ?? false}
            onChange={(v) => set({ newsletter_opt_in: v })}
          />
        </CardContent>
      </Card>

      {/* AI / LLM key (BYOK) */}
      <LlmKeyCard />

      {/* Session */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Session</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Button variant="outline" className="w-fit" onClick={signOut}>
            <LogOut className="size-4" />
            Se déconnecter
          </Button>
        </CardContent>
      </Card>

      {/* Danger zone */}
      <DangerZone />
    </div>
  );
}

function PlanCard() {
  const { isPro, planStatus, planRenewsAt } = usePlan();
  const [busy, setBusy] = useState(false);

  async function openPortal() {
    setBusy(true);
    try {
      const { url } = await api.billingPortal();
      window.location.assign(url);
    } catch {
      toast.error("Impossible d'ouvrir la gestion de l'abonnement.");
      setBusy(false);
    }
  }

  return (
    <Card className={isPro ? "border-primary/40" : undefined}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Crown className="size-4 text-primary" />
          Abonnement
          {isPro ? (
            <Badge className="ml-1">Pro</Badge>
          ) : (
            <Badge variant="secondary" className="ml-1">Gratuit</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {isPro ? (
          <>
            <p className="text-sm text-foreground">
              Vous êtes membre <span className="font-medium text-primary">Boursicote Pro</span> —
              suivis illimités, alertes en temps réel et historique complet.
            </p>
            {planRenewsAt && (
              <p className="text-xs text-foreground-muted">
                {planStatus === "canceled"
                  ? `Accès jusqu'au ${fromNowFR(planRenewsAt)}.`
                  : `Renouvellement ${fromNowFR(planRenewsAt)}.`}
              </p>
            )}
            <Button variant="outline" className="w-fit" disabled={busy} onClick={openPortal}>
              {busy ? "Ouverture…" : "Gérer mon abonnement"}
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-foreground-muted max-w-lg">
              Le plan gratuit est limité à 3 suivis et n'inclut pas les alertes en temps réel.
              Passez à Pro pour un suivi illimité et être prévenu à la seconde.
            </p>
            <Button className="w-fit" onClick={() => openUpgrade()}>
              <Crown className="size-4" />
              Passer à Boursicote Pro
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

const LLM_PROVIDERS: { value: LlmProvider; label: string; placeholder: string }[] = [
  { value: "openrouter", label: "OpenRouter", placeholder: "openai/gpt-4o-mini" },
  { value: "openai", label: "OpenAI", placeholder: "gpt-4o-mini" },
  { value: "anthropic", label: "Anthropic", placeholder: "claude-3-5-haiku-latest" },
];

function LlmKeyCard() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["llm-key-status"], queryFn: () => api.llmKeyStatus() });
  const [provider, setProvider] = useState<LlmProvider>("openrouter");
  const [key, setKey] = useState("");
  const [model, setModel] = useState("");
  const [busy, setBusy] = useState(false);

  const meta = LLM_PROVIDERS.find((p) => p.value === provider)!;
  const existing = data?.keys ?? [];

  async function save() {
    if (key.trim().length < 8) {
      toast.error("Clé invalide.");
      return;
    }
    setBusy(true);
    try {
      await api.saveLlmKey({ provider, key: key.trim(), model: model.trim() || undefined });
      setKey("");
      setModel("");
      await qc.invalidateQueries({ queryKey: ["llm-key-status"] });
      await qc.invalidateQueries({ queryKey: ["me"] });
      toast.success("Clé validée et enregistrée (chiffrée).");
    } catch (e: any) {
      toast.error(e?.body?.detail ? `Clé refusée : ${e.body.detail}` : "Clé refusée par le fournisseur.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(p: LlmProvider) {
    try {
      await api.deleteLlmKey(p);
      await qc.invalidateQueries({ queryKey: ["llm-key-status"] });
      toast.success("Clé supprimée.");
    } catch {
      toast.error("Erreur.");
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="size-4" />
          Résumés IA (votre clé)
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="text-xs text-foreground-muted max-w-lg">
          Ajoutez votre propre clé API pour générer des résumés de sujets, d'actualités et
          d'analyses de cours. La clé est validée puis stockée chiffrée ; elle n'est jamais
          renvoyée ni transmise au moteur de scraping.
        </p>

        {existing.length > 0 && (
          <div className="flex flex-col gap-2">
            {existing.map((k) => (
              <div
                key={k.provider}
                className="flex items-center justify-between gap-3 rounded-md border border-border p-3"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <KeyRound className="size-4 text-foreground-muted shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {LLM_PROVIDERS.find((p) => p.value === k.provider)?.label ?? k.provider}
                      <span className="text-foreground-muted font-normal"> · …{k.last4}</span>
                    </p>
                    <p className="text-xs text-foreground-muted truncate">
                      {k.model || data?.defaults[k.provider]}
                    </p>
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => remove(k.provider)} className="shrink-0">
                  <Trash2 className="size-3.5" />
                  Supprimer
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-3 rounded-md border border-border p-3">
          <NativeSelect
            label="Fournisseur"
            value={provider}
            onValueChange={(v) => setProvider(v as LlmProvider)}
            options={LLM_PROVIDERS.map((p) => ({ value: p.value, label: p.label }))}
          />
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-foreground-muted">Clé API</span>
            <Input
              type="password"
              autoComplete="off"
              placeholder="sk-…"
              value={key}
              onChange={(e) => setKey(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-foreground-muted">
              Modèle (optionnel)
            </span>
            <Input
              placeholder={meta.placeholder}
              value={model}
              onChange={(e) => setModel(e.target.value)}
            />
          </div>
          <Button onClick={save} disabled={busy} className="w-fit">
            {busy ? "Validation…" : "Enregistrer la clé"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  desc,
  value,
  onChange,
}: {
  label: string;
  desc: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-md border border-border p-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-foreground-muted max-w-sm">{desc}</p>
      </div>
      <Button size="sm" variant={value ? "default" : "outline"} onClick={() => onChange(!value)} className="shrink-0 self-start sm:self-auto">
        {value ? "Activé" : "Désactivé"}
      </Button>
    </div>
  );
}

function DangerZone() {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  async function remove() {
    setBusy(true);
    try {
      await api.deleteAccount();
      setAuthToken(null);
      toast.success("Compte supprimé.");
      window.location.assign("/login");
    } catch {
      toast.error("Erreur lors de la suppression.");
      setBusy(false);
    }
  }

  return (
    <Card className="border-destructive/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-destructive">
          <Trash2 className="size-4" />
          Clôturer mon compte
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-foreground-muted">
          Supprime définitivement votre compte, vos suivis et vos alertes. Cette
          action est irréversible.
        </p>
        {confirming ? (
          <div className="flex gap-2 flex-wrap">
            <Button variant="destructive" disabled={busy} onClick={remove}>
              {busy ? "Suppression…" : "Confirmer la suppression"}
            </Button>
            <Button variant="outline" disabled={busy} onClick={() => setConfirming(false)}>
              Annuler
            </Button>
          </div>
        ) : (
          <Button variant="outline" className="w-fit text-destructive" onClick={() => setConfirming(true)}>
            Supprimer mon compte
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// Clean sign-out : invalidate the Better Auth session AND drop the local Bearer
// token, then hard-redirect so all in-memory state is cleared.
async function signOut() {
  try {
    await fetch(`${apiBase}/api/auth/sign-out`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    /* ignore */
  }
  setAuthToken(null);
  window.location.assign("/login");
}
